import { useContext, useState } from 'react';
import { getApolloContext } from '@apollo/client/react';
import { useAiChatContext } from 'juneau';
import { ChatNudgeOutcome, isAlertChatNudge } from '@insio/shared-types';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { CardActions, CardButton, ResultCard, ResultCardHeader } from '../parts/ResultCard';
import { armNudge } from './armed-nudge';
import { respondToChatNudge } from './chat-nudge.api';
import type { NudgePartType } from './nudge.types';

type Answer = 'started' | 'snoozed' | 'dismissed';

/** A proactive offer or alert: start the guided turn, ask again later, or never. */
export function NudgePart({ part }: { part: NudgePartType }) {
  const t = useFeatureTranslation('aiChat');
  const { sendMessageWithText, isLoading } = useAiChatContext();
  const { client } = useContext(getApolloContext());
  const [answer, setAnswer] = useState<Answer | null>(null);
  const keys = `nudges.${part.nudgeId}`;
  // An alert is never retired, so its answers promise silence only until something new happens.
  const answered = isAlertChatNudge(part.nudgeId) ? 'nudges.answeredAlert' : 'nudges.answered';

  const start = () => {
    // A turn already streaming would swallow the armed offer into the wrong message.
    if (isLoading) return;
    setAnswer('started');
    // The next message accepts the offer; the server re-checks it before any AI runs.
    armNudge(part.nudgeId);
    void sendMessageWithText(t(`${keys}.startPrompt`));
  };

  const decline = (
    outcome: typeof ChatNudgeOutcome.DISMISSED | typeof ChatNudgeOutcome.SNOOZED
  ) => {
    setAnswer(outcome === ChatNudgeOutcome.DISMISSED ? 'dismissed' : 'snoozed');
    if (client) void respondToChatNudge(client, part.nudgeId, outcome);
  };

  return (
    <ResultCard>
      <ResultCardHeader title={t(`${keys}.title`)} />
      <span className='text-sm text-foreground-muted'>{t(`${keys}.description`)}</span>
      {answer === null ? (
        <CardActions>
          <CardButton tone='outline' onClick={() => decline(ChatNudgeOutcome.DISMISSED)}>
            {t('nudges.dismiss')}
          </CardButton>
          <CardButton tone='outline' onClick={() => decline(ChatNudgeOutcome.SNOOZED)}>
            {t('nudges.snooze')}
          </CardButton>
          <CardButton onClick={start}>{t(`${keys}.start`)}</CardButton>
        </CardActions>
      ) : (
        <span className='text-sm text-foreground-muted'>{t(`${answered}.${answer}`)}</span>
      )}
    </ResultCard>
  );
}
